'use client';

function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useState, useCallback, useMemo } from 'react';
import { TextField } from '../TextField';
import { Container, CompatIcon } from './elements';
import { PredictiveDropdown } from './PredictiveDropdown';
import { Announcer } from './Announcer';
import { InternalProvider, useId } from '../../util';
const defaultAssistiveHelperText = 'When autocomplete results are available use up and down arrows to review and enter to select. Touch device users, explore by touch or with swipe gestures.';

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const PredictiveTextField = /*#__PURE__*/React.forwardRef(({
  id: incomingId,
  label,
  fieldStatus,
  value = '',
  suggestions = [],
  handleChange,
  iconSrc,
  affixLeftClickable = false,
  onSelection,
  className,
  disableHighlight = false,
  includeSubheader = true,
  suggestWhenEmpty = false,
  alertNoResults = true,
  maxSuggestionsToShow,
  resultsAnnouncement,
  noResultsAnnouncement,
  resultSelectedAnnouncement,
  shouldFilterSuggestions,
  assistiveHelperText = defaultAssistiveHelperText,
  noResultsText,
  shouldFocusFirstItem,
  maxHeight,
  dropdownListItemClassName,
  ...restProps
}, fRef) => {
  const [focused, setFocused] = useState(false);
  const [keyword, setKeyword] = useState(value);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const id = useId('predictive-text', incomingId);
  const onChange = useCallback(value => {
    handleChange === null || handleChange === void 0 || handleChange(value);
    setFocused(true);
    !disableHighlight && setKeyword(value);
    setActiveSuggestionIndex(-1);
  }, []);
  const showSuggestions = useMemo(() => focused && (value.length > 0 || suggestWhenEmpty), [focused, value, suggestWhenEmpty]);
  const filteredSuggestions = useMemo(() => {
    const input = value.toLowerCase();
    const returnValue = shouldFilterSuggestions ? suggestions.filter(({
      header
    }) => header.toLowerCase().includes(input)).slice(0, maxSuggestionsToShow) : suggestions;
    if (shouldFocusFirstItem && returnValue.length > 0) {
      setActiveSuggestionIndex(0);
    }
    return returnValue;
  }, [suggestions, value, shouldFilterSuggestions, maxSuggestionsToShow]);
  const scrollDropdown = activeSuggestionIndex => {
    var _document, _filteredSuggestions$;
    const item = (_document = document) === null || _document === void 0 ? void 0 : _document.getElementById((_filteredSuggestions$ = filteredSuggestions[activeSuggestionIndex]) === null || _filteredSuggestions$ === void 0 ? void 0 : _filteredSuggestions$.id);
    item === null || item === void 0 || item.scrollIntoView({
      block: 'center'
    });
  };
  const handleKeyDown = useCallback(e => {
    if (e.key === 'Enter') {
      if (activeSuggestionIndex === -1) return;
      e.preventDefault();
      setFocused(false);
      setActiveSuggestionIndex(-1);
      onSelection(filteredSuggestions[activeSuggestionIndex]);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (activeSuggestionIndex === -1) return;
      if (maxHeight) {
        setActiveSuggestionIndex(activeSuggestionIndex - 1);
        scrollDropdown(activeSuggestionIndex);
      } else {
        setActiveSuggestionIndex((activeSuggestionIndex + filteredSuggestions.length - 1) % filteredSuggestions.length);
      }
    } else if (e.key === 'ArrowDown') {
      if (maxHeight) {
        if (activeSuggestionIndex === filteredSuggestions.length - 1) return;
        setActiveSuggestionIndex(activeSuggestionIndex + 1);
        scrollDropdown(activeSuggestionIndex);
      } else {
        setActiveSuggestionIndex((activeSuggestionIndex + 1) % filteredSuggestions.length);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setFocused(false);
      setActiveSuggestionIndex(-1);
    }
  }, [activeSuggestionIndex, filteredSuggestions]);
  return /*#__PURE__*/React.createElement(InternalProvider, null, /*#__PURE__*/React.createElement(Container, {
    "data-aui": "predictive-text-field",
    className: className,
    onFocus: () => {
      setFocused(true);
      if (shouldFocusFirstItem && filteredSuggestions.length > 0) {
        setActiveSuggestionIndex(0);
      }
    },
    onBlur: () => {
      setFocused(false);
      setActiveSuggestionIndex(-1);
    }
  }, /*#__PURE__*/React.createElement(Announcer, {
    query: keyword,
    resultCount: filteredSuggestions.length,
    selectedResultIndex: activeSuggestionIndex,
    selectedResult: filteredSuggestions === null || filteredSuggestions === void 0 ? void 0 : filteredSuggestions[activeSuggestionIndex],
    resultsAnnouncement: resultsAnnouncement,
    noResultsAnnouncement: noResultsAnnouncement,
    resultSelectedAnnouncement: resultSelectedAnnouncement
  }), /*#__PURE__*/React.createElement(TextField, _extends({}, restProps, {
    id: id,
    assistiveHelperText: assistiveHelperText,
    "data-testid": "model-predictive-text-input",
    role: "combobox",
    "aria-controls": `${id}-list`,
    "aria-expanded": showSuggestions,
    "aria-autocomplete": "list",
    autoComplete: "off",
    value: value,
    ref: fRef,
    fieldStatus: fieldStatus,
    onChange: ({
      target
    }) => onChange(target.value),
    label: label,
    affixLeft: iconSrc && /*#__PURE__*/React.createElement(CompatIcon, {
      src: iconSrc,
      size: 'large'
    }),
    affixLeftClickable: iconSrc && affixLeftClickable,
    affixRightClickable: true,
    affixRight: value.length > 0 && /*#__PURE__*/React.createElement(CompatIcon, {
      src: "close-x",
      onMouseDown: event => event.preventDefault(),
      size: 'large',
      onClick: () => onChange('')
    }),
    onKeyDown: handleKeyDown
  })), showSuggestions && /*#__PURE__*/React.createElement(PredictiveDropdown, {
    ariaLabelledby: id,
    listId: `${id}-list`,
    "data-testid": 'model-predictive-text-dropdown-container',
    onSelection: onSelection,
    keyword: keyword,
    fieldStatus: fieldStatus,
    setFocused: setFocused,
    suggestions: filteredSuggestions,
    includeSubheader: includeSubheader,
    alertNoResults: alertNoResults,
    activeSuggestionIndex: activeSuggestionIndex,
    setActiveSuggestionIndex: setActiveSuggestionIndex,
    noResultsText: noResultsText,
    maxHeight: maxHeight,
    dropdownListItemClassName: dropdownListItemClassName
  })));
});
export default PredictiveTextField;